import {
	Box,
	Container,
	LoadingOverlay,
	ScrollArea,
	SegmentedControl,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import ActivityTable from "../components/activity/ActivityTable";
import EmptyState from "../components/common/EmptyState";
import LoadingSpinner from "../components/common/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import Pagination from "../components/common/Pagination";
import {
	fetchActivityPage,
	setCategory,
	setPage,
} from "../store/activity/activitySlice";
import { CATEGORY_LABELS } from "../utils/activity";

import type { ActivityCategory } from "../services/api";
import type { AppDispatch, RootState } from "../store/store";

const ALL_CATEGORIES = "all";

const CATEGORY_OPTIONS = [
	{ label: "All", value: ALL_CATEGORIES },
	...Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label })),
];

function ActivityPage() {
	const dispatch = useDispatch<AppDispatch>();
	const { logs, total, page, limit, category, loading, error } = useSelector(
		(state: RootState) => state.activity,
	);

	useEffect(() => {
		dispatch(fetchActivityPage({ page, category }))
			.unwrap()
			.catch((rejection: unknown) => {
				// A 401 arrives as a thrown error rather than a message: the
				// interceptor is already taking the user to /login
				if (typeof rejection === "string") {
					notifications.show({
						title: "Error",
						message: rejection,
						color: "red",
					});
				}
			});
	}, [dispatch, page, category]);

	const handleCategoryChange = (value: string) => {
		dispatch(
			setCategory(value === ALL_CATEGORIES ? null : (value as ActivityCategory)),
		);
	};

	return (
		<Container size="xl">
			<PageHeader
				title="Activity"
				description="Who changed what, and when - across licenses, stations, accounts, users and sign-ins"
			/>

			{/* Seven segments outgrow a phone screen; scrolling beats squeezing */}
			<ScrollArea
				type="auto"
				mb="md">
				<SegmentedControl
					data={CATEGORY_OPTIONS}
					value={category ?? ALL_CATEGORIES}
					onChange={handleCategoryChange}
				/>
			</ScrollArea>

			{loading && logs.length === 0 ? (
				<LoadingSpinner />
			) : error ? (
				<EmptyState
					title="Couldn't load activity"
					message="The page controls below still work - try again, or go back to a page that loaded."
				/>
			) : logs.length === 0 ? (
				<EmptyState
					title="No activity found"
					message={
						category
							? "Nothing has been recorded in this category yet."
							: "Nothing has been recorded yet."
					}
				/>
			) : (
				<Box pos="relative">
					<LoadingOverlay
						visible={loading}
						overlayProps={{ blur: 1 }}
					/>
					<ActivityTable logs={logs} />
				</Box>
			)}

			{/* Outside the branches above, so a failed or loading page never
			    takes the controls needed to leave it */}
			<Pagination
				currentPage={page}
				totalPages={Math.ceil(total / limit)}
				onPageChange={(nextPage) => dispatch(setPage(nextPage))}
			/>
		</Container>
	);
}

export default ActivityPage;
